import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  Segment,
  Header,
  Table,
  Button,
  Loader,
  Message,
  Modal,
  Icon,
} from "semantic-ui-react";
import {
  getDS82StuckBotApplications,
  // Reprocessing functionality commented out for now
  // reprocessStuckBotApplication,
} from "../../../../../apis/stuckBotApi";
import { usePagination } from "../../../../../hooks/usePagination.hook";
import { getDateFormat } from "../../../../../common/date-format";
import toastr from "toastr";
import confirmPopup from "../../../../../modules/confirmPopup";
import { getApplicantStatusString } from "../../../../routers/DashboardRouter/routers/Products/constants/applicantStatuses";

// Wrapper function to transform API response
const getStuckBotApplicationsWrapper = async (params) => {
  const response = await getDS82StuckBotApplications(params);

  // Transform the API data to match what our UI expects
  const transformedData = (response.data.data || []).map((app) => ({
    _id: app._id,
    applicationId: app.applicationId || app._id, // Use the ID if applicationId is not available
    applicantName:
      `${app.firstName || ""} ${app.middleName || ""} ${
        app.lastName || ""
      }`.trim() + ` (${app.accountEmail || app.submissionEmail || ""})`,
    status: app.status,
    botType:
      app.botRunsHistory && app.botRunsHistory.length > 0
        ? app.botRunsHistory[0].type
        : "Unknown",
    errorMessage: app.errorMessage || "No specific error message",
    errorStack: app.errorStack,
    updatedAt: app.updatedAt,
    createdAt: app.createdAt,
    botRuns: app.botRunsHistory?.length || 0,
    botRunsHistory: app.botRunsHistory || [],
    // Include the original data for debugging
    originalData: app,
  }));

  // Return data in the format expected by usePagination
  return {
    data: {
      items: transformedData,
      totalItems: response.data.pagination?.total || transformedData.length,
      totalPages: Math.ceil(
        (response.data.pagination?.total || transformedData.length) /
          (response.data.pagination?.limit || 20),
      ),
      currentPage:
        Math.floor(
          (response.data.pagination?.skip || 0) /
            (response.data.pagination?.limit || 20),
        ) + 1,
    },
  };
};

// Define the table columns for the stuck bot applications
const columns = [
  {
    label: "Application ID",
    prop: "applicationId",
    width: 2,
    getValue: (id, application) => {
      return `<a target="_blank" href="/ds82/applicant/${application._id}">${application._id}</a>`;
    },
    renderHTML: true,
  },
  {
    label: "Applicant Name",
    prop: "applicantName",
    width: 2,
  },
  {
    label: "Status",
    prop: "status",
    width: 1,
    getValue: (status) => {
      return getApplicantStatusString(status) || `Status ${status}`;
    },
  },
  {
    label: "Bot Type",
    prop: "botType",
    width: 1,
  },
  {
    label: "Bot Runs",
    prop: "botRuns",
    width: 1,
  },
  {
    label: "Last Run",
    prop: "lastRun",
    width: 2,
    getValue: (_, application) => {
      const botRunsHistory = application.botRunsHistory || [];
      if (botRunsHistory.length === 0) return "No runs recorded";

      const lastRun = botRunsHistory[botRunsHistory.length - 1];
      return `${lastRun.type} (${getDateFormat(lastRun.createdAt)})`;
    },
  },
  {
    label: "Last Updated",
    prop: "updatedAt",
    width: 2,
    getValue: (fieldValue) => (fieldValue ? getDateFormat(fieldValue) : ""),
  },
  {
    label: "Created At",
    prop: "createdAt",
    width: 2,
    getValue: (fieldValue) => (fieldValue ? getDateFormat(fieldValue) : ""),
  },
];

const DS82StuckBot = () => {
  // State for detail modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [showRawData, setShowRawData] = useState(false);

  // Setup pagination for the stuck bot applications
  const {
    items: applications,
    Paginator,
    loadItems: loadApplications,
    refreshItems: refreshApplications,
    isLoading,
    totalItems,
  } = usePagination(
    getStuckBotApplicationsWrapper,
    {
      initialLoad: true,
      clearOnLoad: true,
      dataProperty: "items",
    },
    null,
  );

  // Load applications on initial load
  useEffect(() => {
    loadApplications(1, {});
  }, [loadApplications]);

  // Handle opening the details modal
  const handleOpenDetails = useCallback((application) => {
    setSelectedApplication(application);
    setDetailModalOpen(true);
  }, []);

  // Handle reprocessing a stuck application
  const handleReprocess = useCallback(
    async (applicantId) => {
      // Reprocessing functionality commented out for now
      /*
      if (
        await confirmPopup(
          "Are you sure you want to reprocess this application?"
        )
      ) {
        try {
          setIsReprocessing(true);
          await reprocessStuckBotApplication(applicantId);
          toastr.success("Application reprocessing has been initiated");
          refreshApplications();
        } catch (error) {
          console.error("Error reprocessing application:", error);
          toastr.error("Failed to reprocess application");
        } finally {
          setIsReprocessing(false);
        }
      }
      */
      toastr.info("Reprocessing functionality not implemented yet");
    },
    [refreshApplications],
  );

  // Render the filter controls (now just a refresh button)
  const renderFilters = useCallback(() => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "1rem",
          gap: "1rem",
          flexWrap: "wrap",
        }}
      >
        <Button primary onClick={refreshApplications}>
          {isLoading ? "Loading..." : "Refresh"}
        </Button>
      </div>
    );
  }, [refreshApplications, isLoading]);

  // Render the details modal
  const renderDetailsModal = useCallback(() => {
    if (!selectedApplication) return null;

    return (
      <Modal
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        size="large"
      >
        <Modal.Header>DS82 Stuck Bot Application Details</Modal.Header>
        <Modal.Content>
          <Button
            onClick={() => setShowRawData(!showRawData)}
            floated="right"
            size="small"
          >
            {showRawData ? "Hide Raw Data" : "Show Raw Data"}
          </Button>

          {showRawData ? (
            <div style={{ marginTop: "20px" }}>
              <h4>Raw Application Data:</h4>
              <pre
                style={{
                  backgroundColor: "#f5f5f5",
                  padding: "10px",
                  overflow: "auto",
                  maxHeight: "600px",
                }}
              >
                {JSON.stringify(
                  selectedApplication.originalData || selectedApplication,
                  null,
                  2,
                )}
              </pre>
            </div>
          ) : (
            <>
              <h3>Application ID: {selectedApplication._id}</h3>
              <h4>Applicant: {selectedApplication.applicantName}</h4>
              <h4>
                Status:{" "}
                {columns
                  .find((col) => col.prop === "status")
                  ?.getValue?.(selectedApplication.status) ||
                  selectedApplication.status}
              </h4>
              <h4>Bot Type: {selectedApplication.botType}</h4>
              <h4>
                Bot Runs: {selectedApplication.botRunsHistory.length ?? 0}
              </h4>
              <h4>Created: {getDateFormat(selectedApplication.createdAt)}</h4>
              <h4>
                Last Updated: {getDateFormat(selectedApplication.updatedAt)}
              </h4>

              <div style={{ marginTop: "20px" }}>
                <h4>Error Details:</h4>
                <pre
                  style={{
                    backgroundColor: "#f5f5f5",
                    padding: "10px",
                    overflow: "auto",
                    maxHeight: "200px",
                  }}
                >
                  {selectedApplication.errorMessage}
                </pre>
              </div>

              {selectedApplication.errorStack && (
                <div style={{ marginTop: "20px" }}>
                  <h4>Error Stack:</h4>
                  <pre
                    style={{
                      backgroundColor: "#f5f5f5",
                      padding: "10px",
                      overflow: "auto",
                      maxHeight: "200px",
                    }}
                  >
                    {selectedApplication.errorStack}
                  </pre>
                </div>
              )}

              <div style={{ marginTop: "20px" }}>
                <h4>Bot Run History:</h4>
                <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                  <Table celled>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>Run ID</Table.HeaderCell>
                        <Table.HeaderCell>Type</Table.HeaderCell>
                        <Table.HeaderCell>Date</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {selectedApplication.botRunsHistory &&
                      selectedApplication.botRunsHistory.length > 0 ? (
                        selectedApplication.botRunsHistory.map((run, index) => (
                          <Table.Row key={index}>
                            <Table.Cell>{run.id}</Table.Cell>
                            <Table.Cell>{run.type}</Table.Cell>
                            <Table.Cell>
                              {getDateFormat(run.createdAt)}
                            </Table.Cell>
                          </Table.Row>
                        ))
                      ) : (
                        <Table.Row>
                          <Table.Cell colSpan="3" textAlign="center">
                            No bot run history available
                          </Table.Cell>
                        </Table.Row>
                      )}
                    </Table.Body>
                  </Table>
                </div>
              </div>
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button color="red" onClick={() => setDetailModalOpen(false)}>
            Close
          </Button>
          {/* Reprocess button commented out for now
          <Button
            color="green"
            onClick={() => handleReprocess(selectedApplication._id)}
            disabled={isReprocessing}
          >
            {isReprocessing ? "Reprocessing..." : "Reprocess Application"}
          </Button>
          */}
        </Modal.Actions>
      </Modal>
    );
  }, [
    detailModalOpen,
    selectedApplication,
    handleReprocess,
    isReprocessing,
    showRawData,
  ]);

  return (
    <React.Fragment>
      {/* Header and filters */}
      <Segment.Group style={{ display: "table", width: "100%" }}>
        <Segment className="tableTitle adminTable" inverted>
          <Header as="h1">DS82 Stuck Bot Applications</Header>
          {renderFilters()}
        </Segment>

        {/* Table of stuck bot applications */}
        {isLoading ? (
          <Segment>
            <Loader active />
          </Segment>
        ) : (
          <Segment>
            {!applications || (applications && applications.length === 0) ? (
              <Message>
                No stuck bot applications found matching the criteria.
              </Message>
            ) : (
              <>
                <div style={{ marginBottom: "1rem" }}>
                  <strong>Total Records: </strong> {totalItems || 0}
                </div>
                <Table celled>
                  <Table.Header>
                    <Table.Row>
                      {columns.map((column) => (
                        <Table.HeaderCell
                          key={column.prop}
                          width={column.width}
                        >
                          {column.label}
                        </Table.HeaderCell>
                      ))}
                      <Table.HeaderCell width={1}>Actions</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {applications &&
                      applications.map((application) => (
                        <Table.Row key={application._id}>
                          {columns.map((column) => (
                            <Table.Cell key={column.prop}>
                              {column.getValue && column.renderHTML ? (
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: column.getValue(
                                      application[column.prop],
                                      application,
                                    ),
                                  }}
                                />
                              ) : column.getValue ? (
                                column.getValue(
                                  application[column.prop],
                                  application,
                                )
                              ) : (
                                application[column.prop]
                              )}
                            </Table.Cell>
                          ))}
                          <Table.Cell>
                            <Button.Group size="tiny">
                              <Button
                                icon
                                title="View application details"
                                onClick={() => handleOpenDetails(application)}
                              >
                                <Icon name="eye" />
                              </Button>
                              {/* Reprocess button commented out for now
                              <Button
                                icon
                                color="green"
                                title="Reprocess application"
                                onClick={() => handleReprocess(application._id)}
                                disabled={isReprocessing}
                              >
                                <Icon name="redo" />
                              </Button>
                              */}
                            </Button.Group>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                  </Table.Body>
                  <Table.Footer>
                    <Table.Row>
                      <Table.HeaderCell colSpan={columns.length + 1}>
                        <Paginator />
                      </Table.HeaderCell>
                    </Table.Row>
                  </Table.Footer>
                </Table>
              </>
            )}
          </Segment>
        )}
      </Segment.Group>

      {/* Detail Modal */}
      {renderDetailsModal()}
    </React.Fragment>
  );
};

export default DS82StuckBot;
